import React, { FC } from "react";
import { Helmet } from "react-helmet-async";
import Input from "shared/Input/Input";
import { Link, useNavigate } from "react-router-dom";
import ButtonPrimary from "shared/Button/ButtonPrimary";
import { useForm } from "react-hook-form";
import { useAuth } from "../../hooks/Auth/useAuth";
import { AuthErrorMessages } from "../../@core/constants/AuthErrorMessages";

export interface PageLoginProps {
  className?: string;
}

interface ILoginForm {
  email: '',
  password: ''
};

const PageLogin: FC<PageLoginProps> = ({ className = "" }) => {
  const { signInWithEmailAndPassword, errorSignIn: error, socials } = useAuth();
  const navigate = useNavigate();
  const { handleSubmit, register } = useForm<ILoginForm>({
    defaultValues: {
      email: '',
      password: ''
    }
  });

  const handleSubmitForm = async (data: ILoginForm) => {
    try {
      const user = await signInWithEmailAndPassword(data.email, data.password);
      if (user) navigate("/account");
    } catch (error) {
      console.error("Error signing in:", error);
    }
  };

  return (
    <div className={`nc-PageLogin ${className}`} data-nc-id="PageLogin">
      <Helmet>
        <title>Login</title>
      </Helmet>
      <div className="container mb-24 lg:mb-32">
        <h2 className="my-20 flex items-center text-3xl leading-[115%] md:text-5xl md:leading-[115%] font-semibold text-neutral-900 dark:text-neutral-100 justify-center">
          Login
        </h2>
        <div className="max-w-md mx-auto space-y-6">
          <div className="grid gap-3">
            {socials.map((item, index) => (
              <div
                key={index}
                onClick={item.onClick}
                className="nc-will-change-transform flex w-full hover:cursor-pointer duration-300 rounded-lg bg-primary-50 dark:bg-neutral-800 px-4 py-3 transform transition-transform sm:px-6 hover:translate-y-[-2px]"
              >
                <img
                  className="flex-shrink-0"
                  src={item.icon}
                  alt={item.name}
                />
                <h3 className="flex-grow text-center text-sm font-medium text-neutral-700 dark:text-neutral-300 sm:text-sm">
                  {item.name}
                </h3>
              </div>
            ))}
          </div>
          {/* OR */}
          <div className="relative text-center">
            <span className="relative z-10 inline-block px-4 font-medium text-sm bg-white dark:text-neutral-400 dark:bg-neutral-900">
              OR
            </span>
            <div className="absolute left-0 w-full top-1/2 transform -translate-y-1/2 border border-neutral-100 dark:border-neutral-800"></div>
          </div>
          {/* FORM */}
          <form className="grid grid-cols-1 gap-6" onSubmit={handleSubmit(handleSubmitForm)}>
            <label className="block">
              <span className="text-neutral-800 dark:text-neutral-200">
                Email address
              </span>
              <Input
                type="email"
                placeholder="example@example.com"
                className="mt-1"
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Invalid email address"
                  }
                })
                }
              />
            </label>
            <label className="block">
              <span className="flex justify-between items-center text-neutral-800 dark:text-neutral-200">
                Password
                <Link to="/forgot-password" className="text-sm">
                  Forgot password?
                </Link>
              </span>
              <Input
                type="password" className="mt-1" {...register("password", {
                required: "Password is required"
              })}/>
            </label>
            {error && (
              <span className="text-red-500 text-sm">
                {`${AuthErrorMessages[error?.message]}` || error.message}
              </span>
            )}
            <ButtonPrimary type="submit">Continue</ButtonPrimary>
          </form>

          {/* ==== */}
          <span className="block text-center text-neutral-700 dark:text-neutral-300">
            New user? {` `}
            <Link to="/signup">Create an account</Link>
          </span>
        </div>
      </div>
    </div>
  );
};

export default PageLogin;
